import { AdobeIMS as _AdobeIMS } from '@identity/imslib'
import { IEnvironment } from '@identity/imslib/adobe-id/IEnvironment'
import { IAdobeIdData } from '@identity/imslib/adobe-id/IAdobeIdData'
import { RootStore } from '@store/store'
import { dispatchAuthStrategy, setAuthToken } from '@store/slices/authSlice'
import { ITokenInformation } from '@identity/imslib/adobe-id/custom-types/CustomTypes'
import {
  defaultAuthStrategy,
  sceneToImageAuthStrategy
} from '@store/middleware/auth/authSaga'
import {
  RedirectFromFirefly,
  RedirectFromQueryParam
} from '@constants/appConstants'

/**
 * This function dispatches the desired auth strategy and is also used as the IMS onReady callback.
 */
export async function startIMSAuthentication() {
  const didRedirectFromFirefly =
    new URLSearchParams(window.location.search).get(RedirectFromQueryParam) ===
    RedirectFromFirefly

  didRedirectFromFirefly
    ? RootStore.dispatch(dispatchAuthStrategy(sceneToImageAuthStrategy))
    : RootStore.dispatch(dispatchAuthStrategy(defaultAuthStrategy))
}

/**
 * This function is called by IMS when a new access token is received.
 * @param tokenInfo - The token information object containing the new access token.
 */
async function onAccessToken(tokenInfo: ITokenInformation) {
  RootStore.dispatch(setAuthToken(tokenInfo.token))
}

/**
 * This function is called by IMS when IMS.refreshToken fails, and also called on app load before onReady if there is no token in storage`.
 */
async function onAccessTokenHasExpired() {
  const token = AdobeIMS.getAccessToken()?.token

  // If there's no token or the refresh already failed once then there's no need to try to refresh again. Also, return early so we aren't stuck in an infinite loop (refreshToken is called again, possibly triggering onAccessTokenHasExpired again, if we don't return early)
  if (!token || AdobeIMS.failedToRefreshToken) {
    AdobeIMS.failedToRefreshToken = false
    return;
  }

  await AdobeIMS.refreshToken()
}

const SCOPE =
  'AdobeID,openid,pps.read,pps.search,firefly_api,account_cluster.read,read_organizations,tk_platform,tk_platform_sync'

const adobeIdData: IAdobeIdData = {
  client_id: process.env.NEXT_PUBLIC_ADOBE_IMS_CLIENT_ID!,
  redirect_uri: process.env.NEXT_PUBLIC_ADOBE_IMS_REDIRECT_PATH,
  scope: SCOPE,
  locale: 'en_US',
  environment:
    process.env.NEXT_PUBLIC_ADOBE_IMS_ENV == 'prd'
      ? IEnvironment.PROD
      : IEnvironment.STAGE,
  useLocalStorage: false,
  autoValidateToken: true,
  onAccessToken,
  onReauthAccessToken: null,
  onError: null,
  onAccessTokenHasExpired,
  onReady: startIMSAuthentication,
  api_parameters: {
    authorize: {
      dctx_id: process.env.NEXT_PUBLIC_ADOBE_IMS_CONTEXT_ID
    }
  }
}

type AdobeIMSType = _AdobeIMS & {
  getAccessTokenAsync: () => Promise<ITokenInformation | null | void>
  isEnterpriseUser: () => Promise<boolean>
  failedToRefreshToken: boolean
}

const AdobeIMS: AdobeIMSType = Object.assign(
  new _AdobeIMS({ ...adobeIdData }),
  { getAccessTokenAsync, isEnterpriseUser, failedToRefreshToken: false }
)

// This function will asynchronously get a new access token if the current one is not valid.
// If it is valid, it will return the current access token.
async function getAccessTokenAsync(): Promise<ITokenInformation | null | void> {
  try {
    const isTokenValid = AdobeIMS.isSignedInUser() // Client-side validation of token against expiration
    if (!isTokenValid) await AdobeIMS.refreshToken()
  } catch (err) {
    console.error('Either token is not valid or could not refresh it. Returning currently stored token.', err)
  } finally {
    const token: ITokenInformation | null = AdobeIMS.getAccessToken()
    return token
  }
}

/**
 * Returns true if the user is an enterprise user. Otherwise it returns false.
 * @param accountType - User's IMS account type
 */
async function isEnterpriseUser(): Promise<boolean> {
  const { account_type: accountType } = await AdobeIMS.getProfile()

  return (
    accountType === 'type2' ||
    accountType === 'type2e' ||
    accountType === 'type3'
  )
}

export default AdobeIMS
